/**
 * Policy templates for the generator (Phase 3). Each template is National guidance content: written fresh
 * to the Baker Tilly writing standards (docs/brand/writing-standards.md), mapped clause by clause to the
 * CIS Controls v8.1 safeguards it helps meet, and informed by the BTI and BTC templates in RAS and PenTest
 * Initiative / Resources / Templates. CIS text is cited, never copied (CIS Controls are CC BY-NC-ND).
 *
 * Placeholders in clause text use {{name}} and are filled from the firm's questionnaire answers.
 */
/** Every questionnaire field, with a sensible suggestion drawn from the implementation guide. */
export const QUESTIONS = {
    firm: { key: 'firm', label: 'Firm name as it should appear', suggestion: '' },
    owner: { key: 'owner', label: 'Policy owner (role)', help: 'The role that keeps this policy current.', suggestion: 'IT Lead' },
    approver: { key: 'approver', label: 'Approved by (role)', help: 'The executive who approves the policy.', suggestion: 'Managing Partner' },
    effectiveDate: { key: 'effectiveDate', label: 'Effective date' },
    reviewCycle: { key: 'reviewCycle', label: 'Review cycle', suggestion: 'every 12 months' },
    itSupport: { key: 'itSupport', label: 'Who runs IT day to day', help: 'For example "our IT team" or "our IT team and our managed service provider".', suggestion: 'our IT team' },
    deviceTool: { key: 'deviceTool', label: 'Device management tool', suggestion: 'Microsoft Intune' },
    identityTool: { key: 'identityTool', label: 'Identity and sign-in service', suggestion: 'Microsoft Entra ID' },
    patchTool: { key: 'patchTool', label: 'Patching tool', suggestion: 'Windows Autopatch and Microsoft Intune' },
    malwareTool: { key: 'malwareTool', label: 'Anti-malware and endpoint protection', suggestion: 'Microsoft Defender for Endpoint' },
    emailTool: { key: 'emailTool', label: 'Email protection', suggestion: 'Microsoft Defender for Office 365' },
    dnsTool: { key: 'dnsTool', label: 'DNS or web filtering', suggestion: 'Cloudflare Gateway' },
    logTool: { key: 'logTool', label: 'Where logs are collected', suggestion: 'Microsoft Purview Audit and Microsoft Sentinel' },
    backupTool: { key: 'backupTool', label: 'Backup service', suggestion: 'Microsoft 365 Backup' },
    trainingTool: { key: 'trainingTool', label: 'Security awareness training', suggestion: 'KnowBe4' },
    passwordTool: { key: 'passwordTool', label: 'Password manager', suggestion: '1Password' },
    dataTool: { key: 'dataTool', label: 'Data classification and loss prevention', suggestion: 'Microsoft Purview sensitivity labels and DLP' },
};
export const COMMON_ASKS = ['firm', 'owner', 'approver', 'effectiveDate', 'reviewCycle', 'itSupport'];
/** Opening and closing sections shared by every policy, so each template only states its own rules. */
export function purposeAndScope(topic, purpose) {
    return {
        heading: 'Purpose and scope',
        clauses: [
            { id: 'purpose', text: purpose, required: true, controls: [] },
            {
                id: 'scope',
                text: `This policy applies to everyone who works for or on behalf of {{firm}}, including partners, staff, students, contractors and service providers, and to every system, device and service that holds or processes {{firm}} or client information. It covers ${topic}.`,
                required: true,
                controls: [],
            },
            {
                id: 'relationship',
                text: '{{firm}} adopts this policy as part of its information security program. It supports the Baker Tilly International Information Security Policy and Resilience Policy. Where this policy and a Baker Tilly International policy differ, the stricter requirement applies.',
                required: true,
                controls: [],
            },
        ],
    };
}
export function closing() {
    return [
        {
            heading: 'Exceptions',
            clauses: [
                {
                    id: 'exceptions',
                    text: 'Any exception to this policy is requested in writing, with the business reason, the risk, the compensating controls and an end date. The {{approver}} approves or declines each exception. Approved exceptions are recorded as risk acceptances in the Cyber Resilience Program portal and reviewed at least once a year.',
                    required: true,
                    controls: [],
                },
            ],
        },
        {
            heading: 'Compliance and review',
            clauses: [
                { id: 'compliance', text: 'The {{owner}} checks that this policy is followed and reports material gaps to the {{approver}}. Breaches may lead to disciplinary action in line with {{firm}} human resources policies.', required: true, controls: [] },
                { id: 'review', text: 'The {{owner}} reviews this policy {{reviewCycle}}, and sooner after a significant change to the business, the technology or the threats {{firm}} faces. The {{approver}} approves each new version.', required: true, controls: [] },
            ],
        },
    ];
}
//# sourceMappingURL=types.js.map